// src/pages/ChatBot/query-logs/corpus-browser.tsx
//
// Left pane of the Custom Answer workbench: a segmented "Documents" /
// "Retrieved chunks" explorer. Documents lists the chatbot corpus with a
// client-side filename filter, opens a PDF in the embedded viewer, and shows
// a floating "Cite selection" button over highlighted passages. Retrieved
// chunks lists the query's chunk trace by rank, each directly citable.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChunkTrace, CustomAnswerCitation } from './types';
import CorpusPdfViewer from './corpus-pdf-viewer';

interface CorpusDoc {
  fileName: string;
  uploadedBy?: string;
  uploadTime?: string;
  roleIDs?: string[];
}

interface CitePrompt {
  left: number;
  top: number;
  quotedText: string;
  pageNumber?: number;
}

interface CorpusBrowserProps {
  chunks: ChunkTrace[];
  chunkTexts: Record<string, string>;
  citations: CustomAnswerCitation[];
  onAddCitation: (citation: CustomAnswerCitation) => void;
  // Changing this re-fits the open PDF to the current pane width — bumped by
  // the workbench when the answer pane collapses/expands (which widens/narrows
  // the document pane). Optional so the browser renders standalone.
  refitToken?: number;
}

type ExplorerTab = 'documents' | 'chunks';

const CHUNK_UNAVAILABLE_TEXT = '[chunk no longer available]';
const CITE_BUTTON_WIDTH_PX = 130;
const CITE_BUTTON_HEIGHT_PX = 34;
const CITE_BUTTON_OFFSET_PX = 6;

// Sort key for the retrieved-chunks tab: best final rank first, falling back
// through rerank → fused; unranked chunks sink to the bottom.
function chunkRankKey(chunk: ChunkTrace): number {
  return chunk.rank.final ?? chunk.rank.rerank ?? chunk.rank.fused ?? Number.MAX_SAFE_INTEGER;
}

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

// Walks up from a node inside the selection to the page wrapper to recover
// which PDF page it sits on.
function findPageNumberForNode(node: Node | null): number | undefined {
  const element = node instanceof Element ? node : node ? node.parentElement : null;
  const pageElement = element ? element.closest('[data-page-number]') : null;
  const parsed = pageElement ? Number(pageElement.getAttribute('data-page-number')) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

// charStart/charEnd index the server-side canonical text and cannot be
// derived from the PDF text layer — intentionally left undefined.
function buildSelectionCitation(documentName: string, prompt: CitePrompt): CustomAnswerCitation {
  return {
    documentName,
    quotedText: prompt.quotedText,
    ...(prompt.pageNumber !== undefined ? { pageNumber: prompt.pageNumber } : {}),
  };
}

function buildChunkCitation(chunk: ChunkTrace): CustomAnswerCitation {
  return {
    documentName: chunk.documentName,
    chunkId: chunk.chunkId,
    ...(chunk.sectionHeader !== undefined ? { sectionHeader: chunk.sectionHeader } : {}),
    ...(chunk.pageNumber !== undefined ? { pageNumber: chunk.pageNumber } : {}),
  };
}

function RetrievedChunkCard({
  chunk,
  chunkText,
  isCited,
  onCite,
}: {
  chunk: ChunkTrace;
  chunkText: string;
  isCited: boolean;
  onCite: () => void;
}) {
  const isUnavailable = chunkText === CHUNK_UNAVAILABLE_TEXT;
  return (
    <div className="qlc-chunk-card">
      <div className="qlc-chunk-head" title={chunk.documentName}>
        <span className="qlc-chunk-doc">{chunk.documentName}</span>
        {chunk.sectionHeader && <span className="qlc-chunk-section"> · {chunk.sectionHeader}</span>}
        {chunk.pageNumber !== undefined && (
          <span className="qlc-chunk-section"> · page {chunk.pageNumber}</span>
        )}
      </div>
      <p className={`qlc-chunk-preview${isUnavailable ? ' qlc-chunk-preview-unavailable' : ''}`}>
        {chunkText}
      </p>
      <button type="button" className="qlc-chip-btn" disabled={isCited} onClick={onCite}>
        {isCited ? 'Cited' : 'Cite'}
      </button>
    </div>
  );
}

export default function CorpusBrowser({
  chunks,
  chunkTexts,
  citations,
  onAddCitation,
  refitToken,
}: CorpusBrowserProps) {
  const [activeTab, setActiveTab] = useState<ExplorerTab>('documents');
  const [docs, setDocs] = useState<CorpusDoc[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docsReloadKey, setDocsReloadKey] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [activeDocName, setActiveDocName] = useState<string | null>(null);
  const [citePrompt, setCitePrompt] = useState<CitePrompt | null>(null);
  const paneRef = useRef<HTMLDivElement | null>(null);
  const viewerWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // Abort only covers the fetch itself; the flag guards the .json() await
    // and non-abort failures from applying state for a superseded run.
    let isCancelled = false;
    setIsLoadingDocs(true);
    setDocsError(null);
    (async () => {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chatbot/uploaded_docs?sort_by=filename&order=asc`,
        { credentials: 'include', signal: controller.signal },
      );
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = (await res.json()) as { uploaded_docs?: CorpusDoc[] };
      if (isCancelled) return;
      setDocs(body.uploaded_docs ?? []);
      setIsLoadingDocs(false);
    })().catch((error: unknown) => {
      if (isCancelled) return;
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setDocsError('Failed to load the document list.');
      setIsLoadingDocs(false);
    });
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [docsReloadKey]);

  // A scroll anywhere inside the viewer pane detaches the floating button
  // from its selection, so hide it (scroll events don't bubble — capture).
  useEffect(() => {
    if (!citePrompt) return;
    const pane = paneRef.current;
    if (!pane) return;
    const hidePrompt = () => setCitePrompt(null);
    pane.addEventListener('scroll', hidePrompt, { capture: true, passive: true });
    return () => pane.removeEventListener('scroll', hidePrompt, { capture: true });
  }, [citePrompt]);

  const filteredDocs = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter((doc) => doc.fileName.toLowerCase().includes(needle));
  }, [docs, searchText]);

  const sortedChunks = useMemo(
    () => [...chunks].sort((a, b) => chunkRankKey(a) - chunkRankKey(b)),
    [chunks],
  );

  const citedChunkIds = useMemo(() => {
    const ids = new Set<string>();
    citations.forEach((citation) => {
      if (citation.chunkId) ids.add(citation.chunkId);
    });
    return ids;
  }, [citations]);

  const handleSelectTab = useCallback((tab: ExplorerTab) => {
    setCitePrompt(null);
    setActiveTab(tab);
  }, []);

  const handleSelectDoc = useCallback((fileName: string) => {
    setCitePrompt(null);
    setActiveDocName(fileName);
  }, []);

  const handleViewerMouseUp = useCallback(() => {
    const selection = window.getSelection();
    const pane = paneRef.current;
    const viewerWrap = viewerWrapRef.current;
    if (!selection || selection.isCollapsed || selection.rangeCount === 0 || !pane || !viewerWrap) {
      setCitePrompt(null);
      return;
    }
    const range = selection.getRangeAt(0);
    if (!viewerWrap.contains(range.commonAncestorContainer)) {
      setCitePrompt(null);
      return;
    }
    const quotedText = collapseWhitespace(selection.toString());
    if (!quotedText) {
      setCitePrompt(null);
      return;
    }
    const rangeRect = range.getBoundingClientRect();
    const paneRect = pane.getBoundingClientRect();
    const left = Math.min(
      Math.max(rangeRect.right - paneRect.left, 0),
      Math.max(paneRect.width - CITE_BUTTON_WIDTH_PX, 0),
    );
    const top = Math.min(
      Math.max(rangeRect.bottom - paneRect.top + CITE_BUTTON_OFFSET_PX, 0),
      Math.max(paneRect.height - CITE_BUTTON_HEIGHT_PX, 0),
    );
    setCitePrompt({
      left,
      top,
      quotedText,
      // startContainer is the selection's first position in document order,
      // so a cross-page highlight reports its first page regardless of
      // whether the user dragged up or down.
      pageNumber: findPageNumberForNode(range.startContainer),
    });
  }, []);

  const handleCiteSelection = useCallback(() => {
    if (!activeDocName || !citePrompt) return;
    onAddCitation(buildSelectionCitation(activeDocName, citePrompt));
    window.getSelection()?.removeAllRanges();
    setCitePrompt(null);
  }, [activeDocName, citePrompt, onAddCitation]);

  const renderDocumentsTab = () => (
    <div className="qlc-docs-layout">
      <div className={`qlc-doc-list-col${activeDocName ? ' qlc-doc-list-col-narrow' : ''}`}>
        <input
          type="text"
          className="qlc-input qlc-doc-search"
          placeholder="Search documents…"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <div className="qlc-doc-list">
          {isLoadingDocs && (
            <div className="qlc-pdf-status">
              <span className="qlc-spinner" />
              Loading…
            </div>
          )}
          {docsError && (
            <div className="qlc-error-banner">
              {docsError}{' '}
              <button
                type="button"
                className="qlc-link-btn"
                onClick={() => setDocsReloadKey((key) => key + 1)}
              >
                Retry
              </button>
            </div>
          )}
          {!isLoadingDocs && !docsError && filteredDocs.length === 0 && (
            <p className="qlc-empty-note">
              {docs.length === 0 ? 'No documents in the corpus.' : 'No documents match the search.'}
            </p>
          )}
          {filteredDocs.map((doc) => (
            <button
              key={doc.fileName}
              type="button"
              className={`qlc-doc-item${doc.fileName === activeDocName ? ' qlc-doc-item-selected' : ''}`}
              title={doc.fileName}
              onClick={() => handleSelectDoc(doc.fileName)}
            >
              {doc.fileName}
            </button>
          ))}
        </div>
      </div>
      {activeDocName && (
        <div ref={paneRef} className="qlc-viewer-pane" onMouseUp={handleViewerMouseUp}>
          <div ref={viewerWrapRef} className="qlc-viewer-wrap">
            <CorpusPdfViewer fileName={activeDocName} refitToken={refitToken} />
          </div>
          {citePrompt && (
            <button
              type="button"
              className="qlc-cite-float"
              style={{ left: citePrompt.left, top: citePrompt.top }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleCiteSelection}
            >
              ＋ Cite selection
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderChunksTab = () => (
    <div className="qlc-chunk-list">
      {sortedChunks.length === 0 ? (
        <p className="qlc-empty-note">No retrieved chunks were logged for this query.</p>
      ) : (
        sortedChunks.map((chunk) => (
          <RetrievedChunkCard
            key={chunk.chunkId}
            chunk={chunk}
            chunkText={chunkTexts[chunk.chunkId] ?? CHUNK_UNAVAILABLE_TEXT}
            isCited={citedChunkIds.has(chunk.chunkId)}
            onCite={() => onAddCitation(buildChunkCitation(chunk))}
          />
        ))
      )}
    </div>
  );

  return (
    <section className="qlc-pane qlc-pane-left">
      <div className="qlc-tabs">
        <button
          type="button"
          className={`qlc-tab${activeTab === 'documents' ? ' qlc-tab-active' : ''}`}
          onClick={() => handleSelectTab('documents')}
        >
          Documents
        </button>
        <button
          type="button"
          className={`qlc-tab${activeTab === 'chunks' ? ' qlc-tab-active' : ''}`}
          onClick={() => handleSelectTab('chunks')}
        >
          Retrieved chunks
        </button>
      </div>
      {activeTab === 'documents' ? renderDocumentsTab() : renderChunksTab()}
    </section>
  );
}
